import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useEffect, useEffectEvent, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { TravellersSelectionCard } from "@/features/trips/components/bookingFlow/booking/TravellersSelectionCard";
import { TripDateSelectionCard } from "@/features/trips/components/bookingFlow/booking/TripDateSelectionCard";
import {
  tripAvailabilityQueryOptions,
  useTripEffectiveStart,
} from "@/features/trips/hooks/useTripAvailability";
import { useTripPrice } from "@/features/trips/hooks/useTripPrice";
import { tripQueryOptions } from "@/features/trips/hooks/useTrips";
import type { BookingData } from "@/features/trips/types/types";
import { useLocaleInfo } from "@/hooks/useLocaleInfo";
import { rudderAnalytics, TripTrackingEvents } from "@/lib/rudderstack";
import { formatCurrency } from "@/utils/currency";

export const Route = createFileRoute("/_authenticated/trips/$tripId/booking")({
  component: RouteComponent,
});

export function RouteComponent() {
  const intl = useIntl();
  const { tripId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { dir } = useLocaleInfo();
  const { data: trip } = useSuspenseQuery(tripQueryOptions(tripId));

  const { getFieldState, formState, setValue, getValues } =
    useFormContext<BookingData>();
  const tripDate = useWatch<BookingData, "tripDate">({ name: "tripDate" });
  const { invalid: tripDateInvalid } = getFieldState("tripDate", formState);
  const { invalid: travellersInvalid } = getFieldState("travellers", formState);
  const invalid = tripDateInvalid || travellersInvalid;
  const { totalPrice } = useTripPrice(trip);

  const { effectiveStartDate, isEffectiveStartLoading } =
    useTripEffectiveStart(trip);

  const setTripDate = useEffectEvent((date: string) => {
    setValue("tripDate", date, { shouldValidate: true });
  });

  const hasDateSelected = !!tripDate;

  useEffect(() => {
    if (hasDateSelected || !effectiveStartDate) return;

    setTripDate(effectiveStartDate);
  }, [effectiveStartDate, hasDateSelected]);

  const { data: availabilityData } = useQuery({
    ...tripAvailabilityQueryOptions(trip.id, tripDate),
    enabled: hasDateSelected && !isEffectiveStartLoading,
  });

  const selectedAvailability = availabilityData?.find(
    ({ date }) => date === tripDate,
  );

  const availableTravellerTypes = useMemo(() => {
    return {
      adultInSingleRoom: !!trip.priceForAdultInASingleRoom,
      adultInDoubleRoom: !!trip.priceForAdultInADoubleRoom,
      childWithBed: !!trip.priceForChildWithBed,
      childWithoutBed: !!trip.priceForChildWithoutBed,
      infant: !!trip.priceForInfant,
    };
  }, [trip]);

  return (
    <>
      <h2 className="mb-4 text-xl font-medium text-text-title">
        {intl.formatMessage({
          defaultMessage: "Select trips date and travellers",
          description: "trips.bookingPage.title",
        })}
      </h2>

      <div className="space-y-3">
        <Controller<BookingData, "tripDate">
          name="tripDate"
          render={({ field: { onChange, value } }) => (
            <TripDateSelectionCard
              trip={trip}
              selectedDate={value}
              onChange={(...args) => {
                onChange(...args);

                // reset travellers selection when date changes
                // we do this to avoid situations where user selects more travellers
                // than available for newly selected date
                setValue(
                  "travellers",
                  {
                    adultInSingleRoom: 0,
                    adultInDoubleRoom: 0,
                    childWithBed: 0,
                    childWithoutBed: 0,
                    infant: 0,
                  },
                  { shouldValidate: true },
                );
              }}
            />
          )}
        />

        <Controller<BookingData, "travellers">
          name="travellers"
          render={({ field: { onChange, value } }) => (
            <TravellersSelectionCard
              spotAvailability={selectedAvailability?.remainingAvailability}
              selectedTravellers={value}
              availableTravellerTypes={availableTravellerTypes}
              onChange={onChange}
            />
          )}
        />

        <p className="text-center text-sm text-text-disabled-primary">
          {intl.formatMessage({
            defaultMessage:
              "We limit packages per date to ensure availability of hotels and tours.",
            description: "trips.bookingPage.spotLimitInfo",
          })}
        </p>
      </div>

      <PersistentDrawer>
        <dl className="mt-2 mb-4 grid grid-cols-2 gap-2 text-sm text-text-primary">
          <dt>
            {intl.formatMessage({
              defaultMessage: "Trip dates",
              description: "trips.tripDates",
            })}
          </dt>
          {/* date ranges in arabic should retail the same logical order as in LTR languages */}
          <dd className="text-end font-bold rtl:text-start" dir="ltr">
            {tripDate ? (
              <>
                <span dir={dir}>{dayjs(tripDate).format("D MMM")}</span> -{" "}
                <span dir={dir}>
                  {dayjs(tripDate)
                    .add(trip.durationInDays, "day")
                    .format("D MMM")}
                </span>
              </>
            ) : (
              "-"
            )}
          </dd>

          <dt>
            {intl.formatMessage({
              defaultMessage: "Total price",
              description: "trips.totalPrice",
            })}
          </dt>
          <dd className="text-end font-bold">{formatCurrency(totalPrice)}</dd>
        </dl>

        <Button
          fullWidth
          variant="contained"
          disabled={invalid}
          onClick={() => {
            rudderAnalytics.track(
              TripTrackingEvents.USER_PROCEEDED_TO_TRIP_TRAVELLERS,
              {
                trip_id: tripId,
                trip_name: trip.name,
              },
            );

            const travellersTypes = getValues("travellers");
            const adults =
              travellersTypes.adultInSingleRoom +
              travellersTypes.adultInDoubleRoom;
            const children =
              travellersTypes.childWithBed + travellersTypes.childWithoutBed;
            const infants = travellersTypes.infant;

            // create new selection values based on current travellers counts
            // and current selection (important when going back from travellers step)
            const currentSlots = getValues("selectedTravellers");
            const mergedSlots = {
              ADT: Array.from(
                { length: adults },
                (_, i) => currentSlots.ADT[i] ?? null,
              ),
              CHD: Array.from(
                { length: children },
                (_, i) => currentSlots.CHD[i] ?? null,
              ),
              INF: Array.from(
                { length: infants },
                (_, i) => currentSlots.INF[i] ?? null,
              ),
            };

            setValue("selectedTravellers", mergedSlots);
            navigate({ to: "../travellers" });
          }}
        >
          {intl.formatMessage({
            defaultMessage: "Continue",
            description: "trips.continue",
          })}
        </Button>
      </PersistentDrawer>
    </>
  );
}
